import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { apiFetch } from '../../api/client';

export interface DisclaimerModalProps {
  open: boolean;
  onClose: () => void;
  slug: 'privacy_policy' | 'cookie_policy' | 'note_legali';
  title: string;
}

export function DisclaimerModal({ open, onClose, slug, title }: DisclaimerModalProps) {
  const { data } = useQuery({
    queryKey: ['documents', slug],
    queryFn: () => apiFetch<{ id: string; slug: string; content: string; updatedAt: string }>(
      `/documents/${slug}`
    ),
    enabled: open,
  });

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 999,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-container)',
          width: '90%',
          maxWidth: 700,
          maxHeight: '80dvh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-title"
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.5rem',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            minHeight: 'auto',
          }}
        >
          <h2 id="disclaimer-title" style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '1.5rem',
            overflow: 'auto',
            flex: 1,
            fontSize: 'var(--text-sm)',
            lineHeight: 1.6,
            color: 'var(--color-text)',
          }}
        >
          {data ? (
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '1.25rem', marginBottom: '0.5rem' }}>
                    {children}
                  </h2>
                ),
                p: ({ children }) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
                ul: ({ children }) => (
                  <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ul>
                ),
                li: ({ children }) => <li style={{ marginBottom: '0.5rem' }}>{children}</li>,
              }}
            >
              {data.content}
            </ReactMarkdown>
          ) : (
            <p style={{ color: 'var(--color-text-muted)' }}>Caricamento...</p>
          )}
        </div>
      </div>
    </>
  );
}
